import { Fragment, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { userSpeakerBulkUpdateCreate } from '@goldcast/api/content';
import { useSearchParams } from 'react-router-dom';
import { isEqual } from 'radash';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { getClipBroadcastId } from '@goldcast/player-config/src/utils/clipUtils';
import {
  selectSpeakerAnalysis,
  selectSpeakerDetails
} from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import SetupContentModal from '../SetupContent/SetupContentModal';
import { RECORDING_DETAILS_STEP } from '../constants';
import { getSetupContentSteps, mapSpeakerToRequest } from './utils';
import { SetupContentStep } from '../types';
import { areSpeakerDetailsChanged, getInitialSpeakerValues } from '../SetupContent/utils';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import useDialog from '@/components/organisms/useDialog';
import { core } from '@/stores/core';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import { useAppContext } from '@/context/AppContext/AppContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { doesSpeakerSpeakInClip } from '@/Pages/Clip/CompositePlayer/CompositePlayerUtils';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { isCustomUpload } from '@/libs/clipContentUtil';
import { getEnv } from '@/libs/env';
import { currentClip, getSpeakerMappingForClip, isFullVideoFaceRecEnabledForClip } from '@/stores/clip';
import useAnalytics from '@/hooks/useAnalytics';
import { getUsedSpeakers } from '@/context/TranscriptContext/TranscriptContextUtils';
import { buildAuthRequestHeaders } from '@/utils/buildAuthRequestHeaders';

export default function IdentifySpeakers() {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const clipStore = useSyncExternalStore(currentClip.subscribe, currentClip.getSnapshot);
  const { logger } = useAppContext();
  const { trackManualSpeakerMappingDone } = useAnalytics();
  const [searchParams] = useSearchParams();
  const [isInlineIdentification, setIsInlineIdentification] = useState(false);

  // Clip flow: activeClipId from the URL. Full-video flow: the recording's full-length asset clip.
  const clipId = useMemo(
    () => searchParams.get('activeClipId') ?? coreStore.content?.full_length_asset_id ?? undefined,
    [searchParams, coreStore.content?.full_length_asset_id]
  );

  const activeClipData = useMemo(() => (clipId ? clipStore[clipId] : undefined), [clipId, clipStore]);

  // Fall back to the content id so speakers still resolve when the clip isn't loaded in the store
  const clipBroadcastId = useMemo(
    () => (activeClipData ? getClipBroadcastId(activeClipData) : (coreStore.content?.id ?? null)),
    [activeClipData, coreStore.content?.id]
  );
  const speakersWithDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));
  const speakerAnalysisForClip = useContentMetadataStore(selectSpeakerAnalysis(clipBroadcastId ?? ''));

  const postIdentifyCallbackRef = useRef<() => void>();

  const [dialogTitle, setDialogTitle] = useState(RECORDING_DETAILS_STEP.title);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogSteps, setDialogSteps] = useState<SetupContentStep[]>([]);
  const [speakerValues, setSpeakerValues] = useState<Record<string, SpeakerWithDetails>>({});

  const { isOpen, openDialog, closeDialog } = useDialog();
  const {
    isOpen: isConfirmationOpen,
    openDialog: openConfirmationDialog,
    closeDialog: closeConfirmationDialog
  } = useDialog();

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.OpenSpeakersIdentification, identifyRecordingSpeakers);
    return () => {
      EventBus.off(CustomEvents.OpenSpeakersIdentification, eventListener);
    };
  }, [clipId, activeClipData, speakersWithDetails, speakerValues]);

  function identifyRecordingSpeakers(params) {
    if (params && params.postIdentifyCallback) {
      postIdentifyCallbackRef.current = params.postIdentifyCallback;
    }
    const isInlineIdentification = !!params?.isInlineIdentification;
    setIsInlineIdentification(isInlineIdentification);
    initSteps(params?.layout, isInlineIdentification);
    openDialog();
  }

  function initSteps(layout?: string, isInlineIdentification?: boolean) {
    const speakers = getUsedSpeakers(speakersWithDetails);
    const resolvedLayout = layout ?? activeClipData?.asset_metadata?.layout;

    const useOnlyClipSpeakers =
      isCustomUpload() &&
      resolvedLayout === 'SPEAKER' &&
      !!clipId &&
      !(activeClipData?.asset_metadata?.scenes && activeClipData.asset_metadata.scenes.length > 1);

    // remove speakers who don't speak in the clip
    // This happens when callback is sent through for active speaker
    let filteredSpeakers = speakers;
    if (useOnlyClipSpeakers && activeClipData) {
      const clipStart = activeClipData.asset_metadata.start;
      const clipEnd = activeClipData.asset_metadata.end;
      filteredSpeakers = speakers.filter(speaker => doesSpeakerSpeakInClip(speaker.speakingSlots, clipStart, clipEnd));
    }
    const steps = getSetupContentSteps(filteredSpeakers, clipId, isInlineIdentification);
    if (steps.length) {
      setDialogTitle(steps[0].title);
      setDialogSteps(steps);
      setSpeakerValues(getInitialSpeakerValues(steps));
    }
  }

  function storeDefaultValues() {
    closeConfirmationDialog();
    closeDialog();
  }

  function saveFaceIdsInVision() {
    if (!clipId) {
      return;
    }
    const speakers = Object.values(speakerValues);

    // Get already existing speaker mappings from the canonical useContentMetadataStore (keyed by content/broadcast id).
    const previousMappings = { ...getSpeakerMappingForClip(clipId) };
    const mappings = { ...previousMappings };

    // update the speaker mappings only for the speakers whose info has been updated
    speakers.forEach(speaker => {
      if (speaker.face_ids?.length) {
        mappings[speaker.id] = speaker.face_ids;
      } else {
        delete mappings[speaker.id];
      }
    });

    if (clipBroadcastId && speakerAnalysisForClip) {
      useContentMetadataStore.getState().updateSpeakerAnalysis({
        contentId: clipBroadcastId,
        speakerAnalysis: { ...speakerAnalysisForClip, speaker_mapping: mappings }
      });
    }

    if (isEqual(mappings, previousMappings)) {
      return;
    }

    const mappingsArray: {
      face_id: number;
      id: string;
    }[] = [];

    // form mappings array from mappings object
    Object.entries(mappings).forEach(mapping => {
      const [id, face_ids] = mapping;
      face_ids.forEach(face_id => {
        mappingsArray.push({
          face_id,
          id
        });
      });
    });

    const isFullVideoFaceRec = activeClipData ? isFullVideoFaceRecEnabledForClip(activeClipData) : false;
    const visionSpeakersId = isFullVideoFaceRec ? clipBroadcastId : clipId;

    if (visionSpeakersId) {
      fetch(`${getEnv('VISION_URL')}/recognize/speakers/${visionSpeakersId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthRequestHeaders()
        },
        mode: 'cors',
        body: JSON.stringify({
          mappings: mappingsArray
        })
      })
        .then(res => res.json())
        .then(() => {
          if (clipId) {
            trackManualSpeakerMappingDone({ clipId });
          }
        })
        .catch(err => {
          logger.error('Error updating speaker mapping', err);
        });
    }
  }

  function save() {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      saveFaceIdsInVision();

      updateExistingSpeakers(speakerValues).then(() => setIsSaving(false));
    } catch (e: any) {
      logger.error('Error saving speaker details', e);
      showErrorToast(e.message);
      setIsSaving(false);
    }
  }

  function runPostIdentifyCallbackRef() {
    if (postIdentifyCallbackRef.current) {
      postIdentifyCallbackRef.current();
    }
  }

  function updateExistingSpeakers(speakerValues: Record<string, SpeakerWithDetails>) {
    return userSpeakerBulkUpdateCreate({
      queryParams: {},
      body: Object.values(speakerValues).map(speaker => {
        return mapSpeakerToRequest(speaker, coreStore.content!.id);
      })
    })
      .then(() => {
        closeDialog();
        showSuccessToast('Speakers updated successfully.');
        updateSpeakersWithDetails(speakerValues);
        runPostIdentifyCallbackRef();
      })
      .catch(() => {
        showErrorToast('We encountered an issue while saving identified speakers. Please try again in a few minutes.');
      });
  }

  function updateSpeakersWithDetails(speakerValues: Record<string, SpeakerWithDetails>) {
    const updatedSpeakers = { ...speakersWithDetails };
    Object.keys(speakerValues).forEach(speakerKey => {
      const speaker = speakerValues[speakerKey];
      updatedSpeakers[speaker.key] = {
        ...updatedSpeakers[speaker.key],
        ...speaker
      };
    });
    EventBus.dispatch(CustomEvents.SpeakersChanged, { updatedSpeakers });
  }

  function closeIdentifySpeakerDialog() {
    if (areSpeakerDetailsChanged(speakerValues, speakersWithDetails)) {
      openConfirmationDialog();
    } else {
      closeDialog();
    }
  }

  return (
    <Fragment>
      {isOpen && dialogSteps.length > 0 && (
        <ContentDialog
          isOpen={isOpen}
          setIsOpen={closeIdentifySpeakerDialog}
          title={dialogTitle}
          disableBackdropClose={true}
          size="medium"
          isHidden={isConfirmationOpen}
          panelClassNames="!max-h-[150vh]"
          titleSize="medium"
        >
          <SetupContentModal
            speakerValues={speakerValues}
            isInlineIdentification={isInlineIdentification}
            steps={dialogSteps}
            setDialogTitle={setDialogTitle}
            onSave={save}
            setSpeakerValues={setSpeakerValues}
            isSaving={isSaving}
            clipId={clipId}
          />
        </ContentDialog>
      )}
      <ConfirmationDialog
        isOpen={isConfirmationOpen}
        onClose={closeConfirmationDialog}
        onConfirm={storeDefaultValues}
        confirmTrackingId="skip-speaker-identification-button"
        title="Close Speaker Identification?"
        content="Closing dialog will discard all unsaved changes."
        confirmLabel="OK"
      />
    </Fragment>
  );
}
